import { useEffect, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { ModalButtonWithForm, SortableList } from 'components/common';
import { useEditorDispatch } from 'components/editor';

import type { Chapter } from 'shared-types';
import type { Editor } from 'types';

interface Props {
  chapters: Editor['editorChapters'];
  currentChapter: Editor['editorCurrentChapter'];
  disabled?: boolean;
}

const EditorChaptersOrder = ({
  chapters,
  currentChapter,
  disabled = false
}: Props) => {
  const { dispatch } = useDispatch();

  const editorDispatch = useEditorDispatch();

  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const [chapterIds, setChapterIds] = useState<number[]>([]);

  useEffect(() => {
    setChapterIds(chapters?.map((chapter) => chapter.id) || []);
  }, [chapters]);

  const handleOrderChange = (keys: number[]) => setChapterIds(keys);

  const handleSubmit = async () => {
    const chapters = chapterIds.map((chapterId, i) => ({
      id: chapterId,
      number: i + 1
    }));

    await api.post(`/admin/courses/${course.id}/chapters-order`, {
      chapters
    });

    const response = await api.get(`admin/courses/${course.id}/chapters`);

    editorDispatch({
      type: 'updateContext',
      update: {
        editorChapters: response.data.chapters,
        editorCurrentChapter: response.data.chapters.find(
          (chapter: Chapter) => chapter.id === currentChapter.id
        )
      }
    });

    dispatch.addNotice({
      text: messages.commonEditOrderSuccess,
      type: 'success'
    });

    return true;
  };

  return (
    <ModalButtonWithForm
      buttonCategory="light"
      buttonDisabled={disabled}
      buttonIcon="arrows-alt-v"
      buttonProps={{
        'aria-label': messages.commonEditOrder,
        'data-tooltip': messages.commonEditOrder
      }}
      contentStyle={{ padding: 0 }}
      modalActionCategory="success"
      modalActionText={messages.commonSave}
      modalTitle={messages.editEditChapterOrder}
      onSubmit={handleSubmit}
    >
      {() => (
        <SortableList
          items={
            chapters && chapters.length
              ? chapters.map((chapter) => ({
                  key: chapter.id,
                  label: chapter.title
                }))
              : []
          }
          itemWithBorder={true}
          listId="editor-chapters-order"
          onChange={handleOrderChange}
        />
      )}
    </ModalButtonWithForm>
  );
};

export default EditorChaptersOrder;
